import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FunctionFlag } from '../../types';

import { convertPayloadForSave, hasFlag } from '../../utils/utils';
import { schema } from './utils/schema';

import { ADMIN_SETTINGS_FUNCTIONS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';

import { ControlledCheckbox, ControlledDatePicker } from 'components/shared';
import ColorPickerPopover from 'components/shared/ColorPickerPopover';
import { HexColor } from 'components/shared/ColorPickerPopover/ColorPickerPopover';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import FunctionGroupSelect from './components/FunctionGroupSelect';

type CreateEditFunctionFormProps = {
  handleClose: () => void;
  rowData?: FunctionFormValues;
};

export type FunctionFlags = {
  FUNCTION_GROUP: boolean;
  REQUEST_EXCLUDE: boolean;
  CAPPLAN_EXCLUDE: boolean;
  BUH_BLOCKED: boolean;
  BONUS_BLOCKED: boolean;
  LEAVE_BLOCKED: boolean;
  SALARY_BLOCKED: boolean;
  DUTY_OVERLAP_ALLOWED: boolean;
  INCLUDE_IN_KIDS_RATIO: boolean;
};

export interface FunctionFormValues {
  code: string;
  description: string;
  colorCode: HexColor;
  codeExt: string;
  costKind: number;
  isGroup: boolean;
  wagePerHour: number;
  flags: FunctionFlag[];
  convertedFlags: FunctionFlags;
  parentFunctionId?: number | null;
  parentFunctionDescription?: string | null;
  parentFunction?: {
    label: string;
    value: number;
  };
  functionId?: number;
  valid: {
    from: Date | null;
    to: Date | null;
  };
}

const CreateEditFunctionForm = ({
  handleClose,
  rowData
}: CreateEditFunctionFormProps) => {
  const { t } = useTranslation('common');

  const { mutateAsync: saveForm, isLoading } = useSaveResource({
    url: ADMIN_SETTINGS_FUNCTIONS,
    id: rowData?.functionId,
    invalidateQueryKey: ['adminFunctions', 'list']
  });

  const convertedFlags: FunctionFlags = {
    BONUS_BLOCKED: hasFlag(FunctionFlag.BONUS_BLOCKED, rowData),
    INCLUDE_IN_KIDS_RATIO: hasFlag(FunctionFlag.INCLUDE_IN_KIDS_RATIO, rowData),
    BUH_BLOCKED: hasFlag(FunctionFlag.BUH_BLOCKED, rowData),
    CAPPLAN_EXCLUDE: hasFlag(FunctionFlag.CAPPLAN_EXCLUDE, rowData),
    DUTY_OVERLAP_ALLOWED: hasFlag(FunctionFlag.DUTY_OVERLAP_ALLOWED, rowData),
    FUNCTION_GROUP: hasFlag(FunctionFlag.FUNCTION_GROUP, rowData),
    LEAVE_BLOCKED: hasFlag(FunctionFlag.LEAVE_BLOCKED, rowData),
    REQUEST_EXCLUDE: hasFlag(FunctionFlag.REQUEST_EXCLUDE, rowData),
    SALARY_BLOCKED: hasFlag(FunctionFlag.SALARY_BLOCKED, rowData)
  };

  const methods = useForm<FunctionFormValues>({
    defaultValues: schema.cast({
      ...rowData,
      // Because <ControlledAutocomplete/> requires a controlled object with a value / label property
      parentFunction:
        rowData?.parentFunctionId && rowData.parentFunctionDescription
          ? {
              value: rowData.parentFunctionId,
              label: rowData.parentFunctionDescription
            }
          : undefined,
      convertedFlags
    }),
    resolver: yupResolver(schema)
  });

  const { control, handleSubmit, setValue, watch } = methods;

  function onColorSelect(color: HexColor) {
    setValue('colorCode', color);
  }

  const onSubmit = async (data: FunctionFormValues) => {
    const request = {
      ...data,
      parentFunctionId: data.parentFunction?.value ?? null,
      parentFunctionDescription: data.parentFunction?.label ?? null
    };
    const payload = convertPayloadForSave(request);
    await saveForm(payload).then(handleClose);
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('functions.formSectionLabels.basis')}
          validateFields={['code', 'description']}
        >
          <Box display="flex" gap={2.5}>
            <TextField
              name="code"
              control={control}
              variant="outlined"
              label={t('functions.fields.code')}
            />
            <Controller
              name="colorCode"
              control={control}
              render={({ field }) => (
                <ColorPickerPopover
                  color={field.value}
                  onColorSelected={onColorSelect}
                  hasEditFeature={true}
                  label={t('functions.fields.colorCode')}
                />
              )}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <TextField
              name="codeExt"
              control={control}
              variant="outlined"
              label={t('functions.fields.codeExt')}
            />
            <TextField
              name="costKind"
              type="number"
              control={control}
              variant="outlined"
              label={t('functions.fields.costKind')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <TextField
              name="description"
              control={control}
              variant="outlined"
              label={t('functions.fields.description')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Section
          title={t('functions.formSectionLabels.functionGroup')}
        >
          <Grid container>
            <Grid item xs={4}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.FUNCTION_GROUP}`}
                label={t('functions.fields.isGroup')}
                disabled={
                  !!watch('parentFunction') &&
                  Object.values(watch('parentFunction') || {}).length > 0
                }
                onChange={({ target: { checked } }) => {
                  if (checked) {
                    setValue('parentFunction', undefined);
                    setValue('parentFunctionId', null);
                    setValue('parentFunctionDescription', null);
                  }

                  setValue('isGroup', checked);
                }}
              />
            </Grid>
            <Grid item xs={8}>
              <FunctionGroupSelect
                control={control}
                disabled={watch(
                  `convertedFlags.${FunctionFlag.FUNCTION_GROUP}`
                )}
              />
            </Grid>
          </Grid>
        </MultiStepForm.Section>
        <MultiStepForm.Section title={t('functions.formSectionLabels.flags')}>
          <Grid container>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.REQUEST_EXCLUDE}`}
                label={t('functions.fields.flags.REQUEST_EXCLUDE')}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.CAPPLAN_EXCLUDE}`}
                label={t('functions.fields.flags.CAPPLAN_EXCLUDE')}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.BUH_BLOCKED}`}
                label={t('functions.fields.flags.BUH_BLOCKED')}
              />
            </Grid>

            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.BONUS_BLOCKED}`}
                label={t('functions.fields.flags.BONUS_BLOCKED')}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.LEAVE_BLOCKED}`}
                label={t('functions.fields.flags.LEAVE_BLOCKED')}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.SALARY_BLOCKED}`}
                label={t('functions.fields.flags.SALARY_BLOCKED')}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.DUTY_OVERLAP_ALLOWED}`}
                label={t('functions.fields.flags.DUTY_OVERLAP_ALLOWED')}
              />
            </Grid>
            <Grid item xs={6}>
              <ControlledCheckbox
                control={control}
                name={`convertedFlags.${FunctionFlag.INCLUDE_IN_KIDS_RATIO}`}
                label={t('functions.fields.flags.INCLUDE_IN_KIDS_RATIO')}
              />
            </Grid>
          </Grid>
        </MultiStepForm.Section>

        <MultiStepForm.Section
          title={t('functions.formSectionLabels.validity')}
          validateFields={['valid.from', 'valid.to']}
        >
          <Box display="flex" gap={2.5}>
            <ControlledDatePicker
              control={control}
              name="valid.from"
              variant="outlined"
              label={t('validFrom')}
            />
            <ControlledDatePicker
              control={control}
              name="valid.to"
              variant="outlined"
              label={t('validTo')}
            />
          </Box>
        </MultiStepForm.Section>

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default CreateEditFunctionForm;
